/*
 * hyperion-index:begin
 * ---
 * path: hyperion-cloud/web/src/capabilities/credential-health/pages/CredentialHealthBrokenCredentials.tsx
 * layer: web
 * area: credential-health
 * kind: page
 * web_root: pages
 * relpath: web/src/capabilities/credential-health/pages/CredentialHealthBrokenCredentials.tsx
 * filename: CredentialHealthBrokenCredentials.tsx
 * extension: tsx
 * ---
 * hyperion-index:end
 */

/**
 * CredentialHealthBrokenCredentials — surfaces credentials CredentialHealth has flagged as
 * broken (consecutive failures, expired passwords, unreachable systems).
 *
 * Until this page existed the backend had a `GET /credential-health/credentials/broken`
 * endpoint and a `POST /credential-health/remediation` endpoint, but no UI to call
 * either of them — broken credentials were invisible unless an MSP went
 * looking through the dashboard summary. Tracked as B4-CredentialHealth in
 * docs/compliance-remediation-plan-2026-04-07.md.
 *
 * Per-row actions:
 *   - Test now: re-runs the credential health check immediately
 *     (POST /credential-health/credentials/{id}/test). The list refetches so a
 *     credential that recovered drops off the broken list straight away.
 *   - Create remediation task: opens a small dialog to choose a remediation
 *     type and submits POST /credential-health/remediation. The credential stays in
 *     the broken list until the task completes and the next health check
 *     marks it healthy — the request just creates the task to track the
 *     work.
 */

import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowPathIcon, KeyIcon, WrenchScrewdriverIcon } from '@heroicons/react/24/outline';

import { api } from '@/platform/api-client/client';
import type { Device } from '@/platform/api-client/types';
import { DataTable, type ColumnDef } from '@/platform/design-system/components/ui/DataTable';
import { ListPage } from '@/platform/design-system/components/list-page';
import { ModalDialog } from '@/platform/design-system/components/ui/ModalDialog';
import { DegradedState } from '@/platform/design-system/components/ui/DegradedState';

interface BrokenCredential extends Record<string, unknown> {
  id: string;
  credentialId: string;
  lastTestStatus?: string;
  lastTestAt?: string;
  lastSuccessAt?: string;
  consecutiveFailures: number;
  isBroken: boolean;
  healthScore?: number;
  passwordAgeDays?: number;
  needsRotation: boolean;
  updatedAt: string;
}

const REMEDIATION_TYPES = [
  { value: 'rotate_ad_password', label: 'Rotate Active Directory password' },
  { value: 'rotate_local_password', label: 'Rotate local account password' },
  { value: 'rotate_sql_login', label: 'Rotate SQL Server login' },
  { value: 'create_service_account', label: 'Create managed service account' },
] as const;

const TEST_TYPES = [
  { value: 'windows_login', label: 'Windows login' },
  { value: 'rdp', label: 'Remote Desktop login' },
  { value: 'sql_server', label: 'SQL Server login' },
  { value: 'service_account_audit', label: 'Service-account audit' },
  { value: 'credential_discovery', label: 'Credential discovery' },
] as const;

interface CredentialActionInput {
  deviceId: string;
  agentId: string;
  operation: string;
  serviceName?: string;
}

function formatRelative(iso: string | undefined): string {
  if (!iso) return 'Never';
  const ms = Date.now() - new Date(iso).getTime();
  if (ms < 0) return 'Just now';
  const mins = Math.floor(ms / 60_000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
}

export function CredentialHealthBrokenCredentials({ embedded: _embedded }: { embedded?: boolean } = {}) {
  const queryClient = useQueryClient();

  const { data, isError, isLoading, isFetching, refetch } = useQuery<BrokenCredential[]>({
    queryKey: ['credential-health', 'credentials', 'broken'],
    queryFn: () => api.get('/credential-health/credentials/broken').then((r) => r.data),
  });

  const credentials = useMemo(() => data ?? [], [data]);

  const [search, setSearch] = useState('');
  const [activeFilter, setActiveFilter] = useState<string | null>(null);
  const [testTarget, setTestTarget] = useState<BrokenCredential | null>(null);
  const [remediateTarget, setRemediateTarget] = useState<BrokenCredential | null>(null);

  const actionDialogOpen = testTarget !== null || remediateTarget !== null;
  const agentDevicesQuery = useQuery<Device[]>({
    queryKey: ['credential-health', 'agent-devices'],
    queryFn: async () => {
      const response = await api.get<{ devices?: Device[]; data?: Device[] } | Device[]>(
        '/devices?agentOnly=true&limit=500'
      );
      const body = response.data;
      const devices = Array.isArray(body) ? body : (body.devices ?? body.data ?? []);
      return devices.filter((device) => Boolean(device.agentId));
    },
    enabled: actionDialogOpen,
    staleTime: 60_000,
  });

  const counts = useMemo(() => {
    const broken = credentials.filter((c) => c.isBroken).length;
    const needsRotation = credentials.filter((c) => c.needsRotation).length;
    const repeated = credentials.filter((c) => c.consecutiveFailures >= 3).length;
    const stale = credentials.filter((c) => {
      if (!c.lastSuccessAt) return true;
      const ms = Date.now() - new Date(c.lastSuccessAt).getTime();
      return ms > 30 * 24 * 60 * 60 * 1000;
    }).length;
    return {
      total: credentials.length,
      broken,
      needsRotation,
      repeated,
      stale,
    };
  }, [credentials]);

  const filtered = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return credentials.filter((c) => {
      if (activeFilter === 'broken' && !c.isBroken) return false;
      if (activeFilter === 'rotation' && !c.needsRotation) return false;
      if (activeFilter === 'repeated' && c.consecutiveFailures < 3) return false;
      if (activeFilter === 'stale') {
        if (!c.lastSuccessAt) return true;
        const ms = Date.now() - new Date(c.lastSuccessAt).getTime();
        if (ms <= 30 * 24 * 60 * 60 * 1000) return false;
      }
      if (!needle) return true;
      return (
        c.credentialId?.toLowerCase().includes(needle) ||
        c.lastTestStatus?.toLowerCase().includes(needle) ||
        false
      );
    });
  }, [credentials, search, activeFilter]);

  const testMutation = useMutation({
    meta: { errorFeedback: 'inline' },
    mutationFn: (req: CredentialActionInput & { credentialId: string }) =>
      api.post(`/credential-health/credentials/${req.credentialId}/test`, {
        testType: req.operation,
        deviceId: req.deviceId,
        agentId: req.agentId,
      }),
    onSuccess: () => {
      setTestTarget(null);
      queryClient.invalidateQueries({ queryKey: ['credential-health', 'credentials', 'broken'] });
    },
  });

  const remediateMutation = useMutation({
    meta: { errorFeedback: 'inline' },
    mutationFn: (req: CredentialActionInput & { credentialId: string }) =>
      api.post('/credential-health/remediation', {
        credentialId: req.credentialId,
        remediationType: req.operation,
        deviceId: req.deviceId,
        agentId: req.agentId,
        serviceName: req.serviceName,
        requiresApproval: true,
      }),
    onSuccess: () => {
      setRemediateTarget(null);
      queryClient.invalidateQueries({ queryKey: ['credential-health'] });
    },
  });

  const columns: ColumnDef<BrokenCredential>[] = useMemo(
    () => [
      {
        key: 'credentialId',
        header: 'Credential',
        sortable: true,
        render: (row) => (
          <span className="font-mono text-xs text-gray-900 dark:text-white">
            {row.credentialId.slice(0, 8)}…
          </span>
        ),
      },
      {
        key: 'lastTestStatus',
        header: 'Last test',
        sortable: true,
        render: (row) => (
          <span
            className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
              row.lastTestStatus === 'success'
                ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                : 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400'
            }`}
          >
            {row.lastTestStatus || 'unknown'}
          </span>
        ),
      },
      {
        key: 'consecutiveFailures',
        header: 'Failures',
        sortable: true,
        render: (row) => (
          <span
            className={`text-sm font-semibold ${
              row.consecutiveFailures >= 3
                ? 'text-red-600 dark:text-red-400'
                : 'text-gray-700 dark:text-gray-300'
            }`}
          >
            {row.consecutiveFailures}
          </span>
        ),
      },
      {
        key: 'lastSuccessAt',
        header: 'Last success',
        sortable: true,
        render: (row) => (
          <span className="text-sm text-gray-600 dark:text-gray-400">
            {formatRelative(row.lastSuccessAt)}
          </span>
        ),
      },
      {
        key: 'passwordAgeDays',
        header: 'Password age',
        sortable: true,
        render: (row) =>
          row.passwordAgeDays != null ? (
            <span
              className={`text-sm ${
                row.passwordAgeDays > 90
                  ? 'text-amber-600 dark:text-amber-400 font-medium'
                  : 'text-gray-600 dark:text-gray-400'
              }`}
            >
              {row.passwordAgeDays}d
            </span>
          ) : (
            <span className="text-sm text-gray-400">—</span>
          ),
      },
      {
        key: 'actions',
        header: '',
        render: (row) => (
          <div className="flex items-center justify-end gap-1.5">
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setTestTarget(row);
              }}
              disabled={testMutation.isPending}
              className="inline-flex items-center gap-1 rounded-lg border border-gray-300 dark:border-gray-600 px-2 py-1 text-xs text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-60 focus:outline-none focus:ring-2 focus:ring-accent-ring focus:ring-offset-2"
              title="Re-test this credential against its system"
            >
              <ArrowPathIcon
                className={`w-3.5 h-3.5 ${testMutation.isPending ? 'motion-loading-spin' : ''}`}
              />
              Test
            </button>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setRemediateTarget(row);
              }}
              className="inline-flex items-center gap-1 rounded-lg bg-emerald-600 text-white px-2.5 py-1 text-xs font-semibold hover:bg-emerald-700 focus:outline-none focus:ring-2 focus:ring-accent-ring focus:ring-offset-2"
              title="Create a remediation task for this credential"
            >
              <WrenchScrewdriverIcon className="w-3.5 h-3.5" />
              Remediate
            </button>
          </div>
        ),
      },
    ],
    [testMutation]
  );

  if (isError || (!isLoading && !data)) {
    return (
      <div className="page-enter p-6">
        <DegradedState
          title="Broken credentials could not be loaded"
          description="The canonical credential-health service did not return a usable response. No healthy or remediated state has been inferred."
          recoveryHint="Credential secret material remains inside Cerberus."
          onRetry={() => void refetch()}
          isRetrying={isFetching}
        />
      </div>
    );
  }

  return (
    <>
      <ListPage<BrokenCredential>
        title="Broken Credentials"
        subtitle="Credential Health credential health — credentials that failed their last test or have stale passwords"
        icon={KeyIcon}
        iconColorClass="text-red-500"
        secondaryActions={[
          {
            label: 'Refresh',
            icon: ArrowPathIcon,
            onClick: () => refetch(),
            loading: isFetching,
          },
        ]}
        tiers={{
          hero: {
            key: 'hero',
            label: 'Healthy',
            value: counts.total - counts.broken,
            sublabel: `of ${counts.total} tracked`,
            tone: 'good',
            expand: [
              {
                key: 'rotation',
                label: 'Needs rotation',
                value: counts.needsRotation,
                tone: 'warn',
              },
              { key: 'stale', label: 'Stale (no success >30d)', value: counts.stale, tone: 'warn' },
              { key: 'repeated', label: 'Repeated failures', value: counts.repeated, tone: 'bad' },
            ],
          },
          medium: [
            { key: 'broken', label: 'Broken', value: counts.broken, tone: 'bad' },
            { key: 'rotation', label: 'Needs rotation', value: counts.needsRotation, tone: 'warn' },
          ],
          activeFilter,
          onFilterChange: setActiveFilter,
        }}
        search={{
          value: search,
          onChange: setSearch,
          placeholder: 'Search credential id or status...',
        }}
        columns={{
          all: columns,
          defaultVisible: [
            'credentialId',
            'lastTestStatus',
            'consecutiveFailures',
            'lastSuccessAt',
            'actions',
          ],
          storageKey: 'credential-health-broken-creds',
        }}
      >
        {({ visibleColumns }) => (
          <DataTable<BrokenCredential>
            data={filtered}
            columns={visibleColumns}
            isLoading={isLoading}
            emptyIcon={KeyIcon}
            emptyMessage={
              counts.total === 0
                ? 'No broken credentials. Run a Credential Health scan to populate this view.'
                : 'No credentials match your filters.'
            }
            pageSize={25}
            keyField="id"
          />
        )}
      </ListPage>

      {remediateTarget && (
        <CredentialActionDialog
          mode="remediate"
          target={remediateTarget}
          devices={agentDevicesQuery.data ?? []}
          devicesLoading={agentDevicesQuery.isLoading}
          devicesError={agentDevicesQuery.isError}
          isPending={remediateMutation.isPending}
          errorMessage={
            remediateMutation.isError
              ? 'The remediation task could not be created. Verify the endpoint and try again.'
              : undefined
          }
          onCancel={() => setRemediateTarget(null)}
          onSubmit={(input) =>
            remediateMutation.mutate({
              credentialId: remediateTarget.credentialId,
              ...input,
            })
          }
        />
      )}
      {testTarget && (
        <CredentialActionDialog
          mode="test"
          target={testTarget}
          devices={agentDevicesQuery.data ?? []}
          devicesLoading={agentDevicesQuery.isLoading}
          devicesError={agentDevicesQuery.isError}
          isPending={testMutation.isPending}
          errorMessage={
            testMutation.isError
              ? 'The credential test could not be queued. Verify the endpoint and try again.'
              : undefined
          }
          onCancel={() => setTestTarget(null)}
          onSubmit={(input) =>
            testMutation.mutate({ credentialId: testTarget.credentialId, ...input })
          }
        />
      )}
    </>
  );
}

function CredentialActionDialog({
  mode,
  target,
  devices,
  devicesLoading,
  devicesError,
  isPending,
  errorMessage,
  onCancel,
  onSubmit,
}: {
  mode: 'test' | 'remediate';
  target: BrokenCredential;
  devices: Device[];
  devicesLoading: boolean;
  devicesError: boolean;
  isPending: boolean;
  errorMessage?: string;
  onCancel: () => void;
  onSubmit: (input: CredentialActionInput) => void;
}) {
  const options = mode === 'test' ? TEST_TYPES : REMEDIATION_TYPES;
  const [operation, setOperation] = useState<string>(options[0].value);
  const [deviceId, setDeviceId] = useState('');
  const [serviceName, setServiceName] = useState('');
  const selectedDevice = devices.find((device) => device.id === deviceId);
  const needsServiceName = mode === 'remediate' && operation === 'create_service_account';
  const canSubmit =
    Boolean(selectedDevice?.agentId) && (!needsServiceName || serviceName.trim().length > 0);

  return (
    <ModalDialog
      title={mode === 'test' ? 'Test credential' : 'Create remediation task'}
      onClose={onCancel}
      dismissible={!isPending}
      overlayClassName="bg-black/60"
      className="w-full max-w-md rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 shadow-xl"
    >
        <div className="border-b border-gray-200 dark:border-gray-700 px-5 py-4">
          <h2 className="text-base font-semibold text-gray-900 dark:text-white">
            {mode === 'test' ? 'Test credential' : 'Create remediation task'}
          </h2>
          <p className="mt-1 text-xs text-gray-600 dark:text-gray-400 font-mono">
            {target.credentialId}
          </p>
        </div>
        <div className="px-5 py-4 space-y-3">
          <label htmlFor="credential-action-operation" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
            {mode === 'test' ? 'Test type' : 'Remediation type'}
          </label>
          <select
            id="credential-action-operation"
            value={operation}
            onChange={(e) => setOperation(e.target.value)}
            className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-2 text-sm text-gray-900 dark:text-white"
          >
            {options.map((t) => (
              <option key={t.value} value={t.value}>
                {t.label}
              </option>
            ))}
          </select>
          <label htmlFor="credential-action-target" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
            Endpoint target
          </label>
          <select
            id="credential-action-target"
            value={deviceId}
            onChange={(e) => setDeviceId(e.target.value)}
            disabled={devicesLoading || devicesError}
            className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-2 text-sm text-gray-900 dark:text-white disabled:opacity-60"
          >
            <option value="">
              {devicesLoading
                ? 'Loading managed endpoints…'
                : devicesError
                  ? 'Managed endpoints could not be loaded'
                  : devices.length === 0
                    ? 'No endpoint agents available'
                    : 'Select an endpoint'}
            </option>
            {devices.map((device) => (
              <option key={device.id} value={device.id}>
                {device.hostname} · {device.status}
              </option>
            ))}
          </select>
          {needsServiceName && (
            <>
              <label htmlFor="credential-action-service-name" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Windows service name
              </label>
              <input
                id="credential-action-service-name"
                value={serviceName}
                onChange={(e) => setServiceName(e.target.value)}
                placeholder="Example: HyperionWorker"
                className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-2 text-sm text-gray-900 dark:text-white"
              />
            </>
          )}
          <p className="text-xs text-gray-600 dark:text-gray-400">
            {mode === 'test'
              ? 'The selected agent resolves the credential from Cerberus only while the test runs; no secret is stored in the command.'
              : 'The task requires explicit approval before execution. Generated passwords remain in Cerberus and are never stored in the command.'}
          </p>
          {errorMessage && (
            <p role="alert" className="text-xs font-medium text-red-600 dark:text-red-400">
              {errorMessage}
            </p>
          )}
        </div>
        <div className="flex items-center justify-end gap-2 border-t border-gray-200 dark:border-gray-700 px-5 py-4">
          <button
            type="button"
            onClick={onCancel}
            disabled={isPending}
            className="rounded-lg border border-gray-300 dark:border-gray-600 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-60 focus:outline-none focus:ring-2 focus:ring-accent-ring focus:ring-offset-2"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() =>
              selectedDevice?.agentId &&
              onSubmit({
                deviceId: selectedDevice.id,
                agentId: selectedDevice.agentId,
                operation,
                serviceName: needsServiceName ? serviceName.trim() : undefined,
              })
            }
            disabled={isPending || !canSubmit}
            className="inline-flex items-center gap-2 rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-700 disabled:opacity-60 focus:outline-none focus:ring-2 focus:ring-accent-ring focus:ring-offset-2"
          >
            {isPending && <ArrowPathIcon className="w-4 h-4 motion-loading-spin" />}
            {isPending
              ? mode === 'test'
                ? 'Queueing…'
                : 'Creating…'
              : mode === 'test'
                ? 'Queue test'
                : 'Create for approval'}
          </button>
        </div>
    </ModalDialog>
  );
}

export default CredentialHealthBrokenCredentials;
